import { type ReactNode, useRef } from 'react';
import { Group, Toolbar } from 'react-aria-components';
import { useBottomLayer } from '../lib/hooks';
import { cx } from './cx';
import styles from './CommandBar.module.css';

export interface CommandBarProps {
  /** Accessible name of the toolbar. */
  label: string;
  /** Create actions (a labelled group at the start of the bar). */
  start?: ReactNode;
  startLabel?: string;
  /** The selected row's tools, or the prompt to select one. */
  selection: ReactNode;
  /** Status at the end of the bar (e.g. "Updating…"). */
  end?: ReactNode;
  testId?: string;
  /** Docks the selection zone at the bottom of the viewport on phones (a row is selected). */
  dockSelection?: boolean;
  className?: string;
}

/**
 * One toolbar directly above a table (DESIGN_SYSTEM §4, §6): a start zone with the create actions
 * and a selection zone with the selected row's tools. React Aria Toolbar: arrow keys move between
 * its controls (mirrored in RTL); Tab leaves the bar. The selection's content marks its name with
 * `commandBarStyles.selectionLabel` and its tools (or prompt) with `commandBarStyles.selectionTools`;
 * on desktop the bar keeps two rows of constant height.
 */
export function CommandBar({
  label,
  start,
  startLabel,
  selection,
  end,
  testId,
  dockSelection,
  className,
}: CommandBarProps) {
  const selectionRef = useRef<HTMLDivElement>(null);
  useBottomLayer(selectionRef, Boolean(dockSelection));
  return (
    <Toolbar aria-label={label} className={cx(styles.bar, className)} data-testid={testId}>
      {start ? (
        <Group aria-label={startLabel} className={styles.start}>
          {start}
        </Group>
      ) : null}
      <div ref={selectionRef} className={cx(styles.selection, dockSelection && styles.docked)}>
        {selection}
      </div>
      {end ? <div className={styles.end}>{end}</div> : null}
    </Toolbar>
  );
}

export const commandBarStyles = styles;
